import { ChangeDetectionStrategy, Component, OnInit, ViewChild, inject } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { AccountObject, AutomatedOfferStatus, Deal, DealPartyE, DealViewRaw, Note, SupplierOffer, TableKey, DEAL_CLOSED, DealRow } from '@tradecafe/types/core'
import { DeepReadonly, isDealClone, isDealConfirmed, pickupDate } from '@tradecafe/types/utils'
import { untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { compact, identity, isEqual, last, orderBy, sortBy, uniq } from 'lodash-es'
import { BehaviorSubject, Observable, ReplaySubject, Subscription, combineLatest, from, merge, of } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, filter, map, mapTo, skip, switchMap, take, tap, withLatestFrom } from 'rxjs/operators'
import { AuthApiService } from 'src/api/auth'
import { loadAccounts, selectAllAccounts } from 'src/app/store/accounts'
import { loadCarriers } from 'src/app/store/carriers'
import { loadCurrencies } from 'src/app/store/currencies'
import { actualizeCostToZeroSuccess, autosaveDealCostSuccess, autosaveDealSegmentSuccess, cloneDealSuccess, copyCloneDealSuccess, escalateInvoiceSuccess, escalateInvoicesSuccess, lockDeal, lockDealFailure, lockDealSuccess, massDealForm, massDealFormFailure, massDealFormSuccess, saveDealForm, saveDealFormFailure, saveDealFormSuccess, sendConfirmationSuccess, saveNoteSuccess, setDealDateRangeSuccess, submitDealForm, submitDealFormFailure, submitDealFormSuccess, unactualizeCostToZeroSuccess } from 'src/app/store/deal-view.actions'
import { selectDealsByIds, selectRawDealsByIds } from 'src/app/store/deal-view.reducer'
import { loadLocations } from 'src/app/store/locations'
import { loadMeasures } from 'src/app/store/measures'
import { loadProducts } from 'src/app/store/products'
import { ConfirmModalService } from 'src/components/confirm/confirm-modal.service'
import { CreditNoteFormService } from 'src/components/credits/credit-note-form/credit-note-form.service'
import { CsvExporterService } from 'src/components/csv-exporter/csv-exporter.service'
import { columnDefsById } from 'src/components/deals-list'
import { NoteFormService } from 'src/components/notes/note-form/note-form.service'
import { NotesOverlayService } from 'src/components/notes/notes-overlay/notes-overlay.service'
import { SegmentFormService } from 'src/components/segment-form/segment-form.service'
import { UploaderDialogService } from 'src/components/uploader-dialog/uploader-dialog.service'
import { SupplierOfferOverlayService } from 'src/pages/admin/auction/supplier-offer-overlay/supplier-offer-overlay.service'
import { DealOverlaysService } from 'src/services/actions/deal-overlays.service'
import { CreditPoolService } from 'src/services/data/credit-pool.service'
import { DealElasticSearchService } from 'src/services/data/deal-elastic.service'
import { DealFormCalculatorService } from 'src/services/data/deal-form-calculator.service'
import { canCreateSupplierOffer } from 'src/services/data/deal-view-permissions.service'
import { canShowAutomatedOfferLink, openAutomatedOffer } from 'src/services/data/automated-offer-link'
import { ADD_FREIGHT_DISABLED_TOOLTIP, isAddFreightDisabledForDeal } from 'src/services/data/metik-inalfresco.utils'
import { DealsService } from 'src/services/data/deals.service'
import { OffersService } from 'src/services/data/offers.service'
import { isManagerOrSuperuser } from 'src/services/data/users.service'
import { FileInfo, PrefillFileIds, ProductAttachmentsService } from 'src/services/data/product-attachments.service'
import { replayForm } from 'src/shared/utils/replay-form'
import { dayjs } from 'src/services/dayjs'
import { environment } from 'src/environments/environment'
import { DealViewIncompleteComponent } from 'src/shared/deal-view-incomplete/deal-view-incomplete.component'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { DealDocumentsService } from '../../deals/deal-documents/deal-documents.service'
import { DocumentsOverlayService } from '../../deals/deal-documents/documents-overlay/documents-overlay.service'
import { RequestPrepaymentFormService } from '../../deals/request-prepayment/request-prepayment.service'
import { SendConfirmationFormService } from '../../deals/send-confirmation-form/send-confirmation-form.service'
import { CreateOfferFormService } from '../create-offer-form/create-offer-form.service'
import { CloneFormService } from '../deal-clones/clone-form/clone-form.service'
import { DealMessagesOverlayService } from '../deal-messages/deal-messages-overlay/deal-messages-overlay.service'
import { ShipmentRatePickerService } from '../deal-shipping/shipment-rate-picker/shipment-rate-picker.service'
import { ShipmentRatePickerService as ShipmentRatePickerServiceUpgraded } from 'src/components/shipment-rate-picker/shipment-rate-picker.service'
import { ChangeBrokeragePartyFormService } from './change-brokerage-party-form/change-brokerage-party-form.service'
import { ChangeCloneFormService } from './change-clone-form/change-clone-form.service'
import { disallowedCloneActionDeals } from './deal-clones/clone-actions-permissions'
import { DealCloneActionsService } from './deal-clones/deal-clone-actions.service'
import { ChangePartyFormService } from './change-party-form/change-party-form.service'
import { DealClonesComponent, FRONTEND_SORT_COLUMNS } from './deal-clones/deal-clones.component'
import { CostFormService } from './deal-details/cost-form/cost-form.service'
import { DealFormPageBaseController } from './deal-form-base.component'
import { DealFormPageOverlayService } from './deal-form-page-overlay/deal-form-page-overlay.component.service'
import { cloneFromSel, copyFromSel, isCloneSel, isCopySel, isLockedSel, isPrimaryCloneSel, isSubmittedSel } from './deal-form-page.decorator'
import { DealFormService } from './deal-form.service'
import { DealFormSnapshotsService } from './deal-form.service-snapshots'
import { MatMenuTrigger } from '@angular/material/menu'
import { DealReferenceNumbersService } from '../../deals/list/actions/deal-reference-numbers/deal-reference-numbers.service'


const TABS = ['details', 'product-specs', 'attachments', 'logistics-costs', 'shipping', 'default-costs', 'clones', 'logs']
@Component({
  selector: 'tc-deal-form-page',
  templateUrl: './deal-form-page.component.html',
  styleUrls: ['./deal-form-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DealFormPageComponent extends DealFormPageBaseController implements OnInit {
  private actions$ = inject(Actions)
  private activeRoute: ActivatedRoute;
  protected AuthApi: AuthApiService;
  private ChangePartyForm = inject(ChangePartyFormService)
  private ChangeCloneForm = inject(ChangeCloneFormService)
  private CloneForm = inject(CloneFormService)
  private ConfirmModal = inject(ConfirmModalService)
  private CreateOfferForm = inject(CreateOfferFormService)
  protected DealDocuments: DealDocumentsService;
  protected DealForm: DealFormService;
  protected DealFormSnapshots = inject(DealFormSnapshotsService)
  private DealFormCalculator = inject(DealFormCalculatorService)
  private NoteForm = inject(NoteFormService)
  protected DealOverlays: DealOverlaysService;
  private Offers = inject(OffersService)
  protected router: Router;
  protected SendConfirmationForm = inject(SendConfirmationFormService)
  protected store: Store;
  private SupplierOfferOverlayService = inject(SupplierOfferOverlayService)
  private CsvExporter = inject(CsvExporterService)
  private elastic = inject(DealElasticSearchService)
  private DocumentsOverlay = inject(DocumentsOverlayService)
  protected UploaderDialog: UploaderDialogService;
  protected dealFormPageOverlayService: DealFormPageOverlayService;
  private readonly ChangeBrokeragePartyForm = inject(ChangeBrokeragePartyFormService)
  private readonly CreditPool = inject(CreditPoolService)
  private readonly DealReferenceNumbersService = inject(DealReferenceNumbersService)
  private readonly CloneActions = inject(DealCloneActionsService)
  private readonly ProductAttachments = inject(ProductAttachmentsService)

  constructor() {
    const activeRoute = inject(ActivatedRoute)
    const AuthApi = inject(AuthApiService)
    const CostForm = inject(CostFormService)
    const CreditNoteForm = inject(CreditNoteFormService)
    const DealDocuments = inject(DealDocumentsService)
    const DealForm = inject(DealFormService)
    const NotesOverlay = inject(NotesOverlayService)
    const DealOverlays = inject(DealOverlaysService)
    const Deals = inject(DealsService)
    const RequestPrepaymentForm = inject(RequestPrepaymentFormService)
    const router = inject(Router)
    const SegmentForm = inject(SegmentFormService)
    const ShipmentRatePicker = inject(ShipmentRatePickerService)
    const ShipmentRatePickerUpgraded = inject(ShipmentRatePickerServiceUpgraded)
    const store = inject(Store)
    const toaster = inject(ToasterService)
    const UploaderDialog = inject(UploaderDialogService)
    const dealFormPageOverlayService = inject(DealFormPageOverlayService)
    const DealMessagesOverlay = inject(DealMessagesOverlayService)

    super(
      '/trading/deals',
      dealFormPageOverlayService.getDealId() ? of(dealFormPageOverlayService.getDealId()) : activeRoute.params.pipe(map(pm => pm.dealId)),
      activeRoute.snapshot.queryParams.back || '/trading/deals',
      AuthApi,
      CostForm,
      CreditNoteForm,
      DealForm,
      NotesOverlay,
      DealOverlays,
      Deals,
      RequestPrepaymentForm,
      router,
      SegmentForm,
      ShipmentRatePicker,
      ShipmentRatePickerUpgraded,
      store,
      toaster,
      UploaderDialog,
      DealDocuments,
      DealMessagesOverlay
    )
  
    this.activeRoute = activeRoute;
    this.AuthApi = AuthApi;
    this.DealDocuments = DealDocuments;
    this.DealForm = DealForm;
    this.DealOverlays = DealOverlays;
    this.router = router;
    this.store = store;
    this.UploaderDialog = UploaderDialog;
    this.dealFormPageOverlayService = dealFormPageOverlayService;
  }

  protected readonly DealPartyE = DealPartyE
  protected readonly WA_13995_SHIPMENT_RATE_PICKER_UPGRADE = environment.WA_13995_SHIPMENT_RATE_PICKER_UPGRADE

  // static options (read from route params and data)
  readonly isNew$ = this.dealId$.pipe(map(dealId => !dealId), distinctUntilChanged())
  readonly title$ = this.isNew$.pipe(map(isNew => isNew ? 'New Deal' : 'Edit Deal'), distinctUntilChanged())
  protected readonly isTrader = this.AuthApi.currentUser.role === 'trader'
  protected readonly isBrokerageDeal$ = this.dealViewRaw$.pipe(map(dv => dv.deal.deal_type === 'brokerage'), distinctUntilChanged())

  // active tab name and index
  private activeTab$ = new BehaviorSubject(location.hash.slice(1) || TABS[0])
  activeTabIndex$ = this.activeTab$.pipe(map(tabName => TABS.indexOf(tabName)), distinctUntilChanged())

  // audit filter
  protected readonly tableIdentity = TableKey.DealAudit;
  protected readonly auditLogFilter$ = this.dealId$.pipe(distinctUntilChanged(), map(dealId => ({ deal_id: dealId})))

  // clones component
  private dealClones$ = new BehaviorSubject<DealClonesComponent>(undefined)
  /* private */get dealClones() { return this.dealClones$.value }
  @ViewChild(DealClonesComponent) set dealClones(dealClones: DealClonesComponent) { this.dealClones$.next(dealClones) }
  @ViewChild(DealViewIncompleteComponent) incomplete: DealViewIncompleteComponent

  noSelectedClones$ = this.dealClones$.pipe(
    switchMap(dealClones => dealClones.selectedIds$),
    map(x => !x.length),
    distinctUntilChanged())

  areSelectedDealsClosed$ = this.dealClones$.pipe(
    switchMap(dealClones => dealClones.selectedIds$),
    switchMap(dealIds => dealIds.length ? this.store.select(selectRawDealsByIds(dealIds)) : of([])),
    map(deals => deals.some(deal => deal?.status === DEAL_CLOSED)),
    distinctUntilChanged()
  )

  private selectedDeals$ = this.dealClones$.pipe(
    switchMap(dc => dc.selectedIds$),
    take(1),
    switchMap(dealIds => this.Deals.getDealViews(dealIds)))

  private dealOrClones$: Observable<DeepReadonly<Deal[]>> = this.activeTab$.pipe(
    switchMap(activeTab => activeTab === 'clones'
      ? this.dealClones$.value.selectedIds$.pipe(
        switchMap(dealIds => this.store.select(selectRawDealsByIds(dealIds))),
        // TODO: WA-7397: sort by pickupDate - provide segments
        map(deals => sortBy(deals, deal => pickupDate(deal))))
      : this.dealRaw$.pipe(map(deal => [deal]))))
  private fetchDealViewOrClones$ = this.dealOrClones$.pipe(
    take(1),
    switchMap(deals => this.fetchDealViews(deals.map(d => d.deal_id))))

  // ui state
  isLocked$ = isLockedSel(this.dealRaw$)
  protected canCreatePrepayment$ = this.dealViewRaw$.pipe(map(dv => dv.can_create_prepayment), distinctUntilChanged())

  // WA-19859: automated-offer link in the action menu (mirrors deals-list).
  // `automated_offer_id` is on `.deal`; `automated_offer_status` sits at the top level
  // of the deal-view payload (requested in DealsService.getDealViews) but isn't on the
  // DealViewRaw type yet, hence the cast.
  protected readonly AutomatedOfferStatus = AutomatedOfferStatus
  protected readonly openAutomatedOffer = openAutomatedOffer
  protected readonly automatedOfferLink$ = this.dealViewRaw$.pipe(
    map((dv) => ({
      automated_offer_id: dv.deal.automated_offer_id,
      automated_offer_status: (dv as DeepReadonly<DealViewRaw & { automated_offer_status?: AutomatedOfferStatus }>).automated_offer_status,
    })),
    map((data) => canShowAutomatedOfferLink(data) ? data : undefined),
    distinctUntilChanged((a, b) =>
      a?.automated_offer_id === b?.automated_offer_id && a?.automated_offer_status === b?.automated_offer_status),
  )
  isSubmitted$ = isSubmittedSel(this.dealRaw$)
  isClone$ = isCloneSel(this.dealRaw$)
  isCopy$ = isCopySel(this.dealRaw$)
  isPrimaryClone$ = isPrimaryCloneSel(this.dealRaw$)
  isClonesTabSelected$ = this.dealId$.pipe(switchMap(dealId => dealId
    ? this.activeTab$.pipe(map(activeTab => activeTab === 'clones'))
    : of(false)))
  isConfirmationDisabled$ = combineLatest([
    this.isNew$,
    this.activeTab$.pipe(
      switchMap(activeTab => activeTab === 'clones'
        ? this.dealClones$.pipe(switchMap(dc => dc.selectedIds$), map(ids => !ids.length))
        : of(false))
    )
  ]).pipe(
    map(([isNew, isClonesDisabled]) => isNew || isClonesDisabled),
    distinctUntilChanged())

  readonly isAddFreightDisabled$ = this.dealRaw$.pipe(
    map(deal => isAddFreightDisabledForDeal(deal, this.AuthApi.currentUser.role === 'trader')),
    distinctUntilChanged(),
  )

  readonly addFreightDisabledTooltip = ADD_FREIGHT_DISABLED_TOOLTIP

  // Flag gates menu visibility only; per-deal eligibility is enforced at click time in withCloneActionTargets.
  protected readonly cloneActionsEnabled = environment.WA_21001_CLONE_TAB_ACTIONS

  canCreateSupplierOffer$ = this.dealRaw$.pipe(
    switchMap(deal => {
      if (!deal?.deal_id) return of(false);
      return combineLatest([
        this.Deals.getDealViewPicky(deal.deal_id, ['deal']),
        this.Deals.findAutomatedOffersByDeal(deal.deal_id),
        from(this.Offers.findSubOffer(deal.deal_id)).pipe(catchError(() => of(null)))
      ]).pipe(
        map(([currentDeal, automatedResult, supplierOffer]) =>
          canCreateSupplierOffer(currentDeal.deal, this.AuthApi.currentUser)
          && !this.hasActiveSupplierOffer(supplierOffer)
          && automatedResult.data.length === 0
        ),
        catchError(() => of(false))
      );
    }),
    distinctUntilChanged()
  );

  hasActiveSupplierOffer = (offer) => {
    return offer && !this.Offers.isExpired(offer) && !this.Offers.isCancelled(offer)
  }

  canNotClone$ = this.dealRaw$.pipe(
    map(deal => !deal.deal_id || isDealClone(deal)),
    distinctUntilChanged())

  cloneFrom$ = cloneFromSel(this.dealRaw$)
  copyFrom$ = copyFromSel(this.dealRaw$)
  supplierOffer: SupplierOffer

  detailsTabInvalid$: Observable<number> = of(0) // this.fb.group({ details: this.detailsForm, products: this.productsForm })
  specsTabInvalid$: Observable<number> = of(0) // this.fb.group({ products: this.productsForm })

  modalDisplay = false
  accounts: DeepReadonly<AccountObject>[] = []

  // WA-22112: deal attachments tab — editable until the deal is confirmed to a real
  // (non-inventory) buyer; after that manager/superuser only (same gate as files-list canDelete)
  protected readonly attachmentFileIds$ = new ReplaySubject<string[]>(1)
  protected readonly prefillAttachments$ = new ReplaySubject<PrefillFileIds | null>(1)
  protected attachmentsPicFileId: string
  protected attachmentsTechSpecFileId: string
  // null until the attachments component reports its state — saving must not wipe unloaded attachments
  protected currentAttachments: FileInfo[] = null
  private lastProfilePrefill: PrefillFileIds

  protected readonly canEditAttachments$ = combineLatest([this.isNew$, this.dealViewRaw$]).pipe(
    map(([isNew, dv]) => isNew || !dv?.deal || !isDealConfirmed(dv.deal) || isManagerOrSuperuser(this.AuthApi.currentUser)),
    distinctUntilChanged(),
  )

  protected onAttachmentsChange(files: FileInfo[]) {
    this.currentAttachments = files

    const controls = this.detailsForm.controls
    const changed = files.some(f => !f.file_id || f.descriptionChanged)
      || !isEqual(files.map(f => f.file_id), controls.attachmentFileIds.value || [])
      || (files.find(f => f.isProductPic)?.file_id || '') !== (controls.productPicFileId.value || '')
      || (files.find(f => f.isTechSpec)?.file_id || '') !== (controls.techSpecFileId.value || '')
    if (changed && !controls.attachmentFileIds.dirty) {
      controls.attachmentFileIds.markAsDirty()
      // markAsDirty doesn't emit statusChanges — nudge the Save button's dirty badge
      controls.attachmentFileIds.updateValueAndValidity()
    }
  }

  ngOnInit(): void {
    this.modalDisplay = !!this.dealFormPageOverlayService.getDealId()
    this.initAttachmentsTab()
    merge(
      this.actions$.pipe(ofType(saveDealForm, massDealForm), mapTo('save')),
      this.actions$.pipe(ofType(submitDealForm), mapTo('submit')),
      this.actions$.pipe(ofType(lockDeal), mapTo('lock')),
      this.actions$.pipe(ofType(saveDealFormSuccess, saveDealFormFailure, massDealFormSuccess, massDealFormFailure, submitDealFormSuccess, submitDealFormFailure, lockDealSuccess, lockDealFailure), mapTo(undefined)),
    ).pipe(distinctUntilChanged(), untilComponentDestroyed(this)).subscribe(this.inProgress$)

    this.actions$.pipe(
      ofType(saveDealFormSuccess, submitDealFormSuccess),
      withLatestFrom(this.isNew$),
      untilComponentDestroyed(this),
    ).subscribe(([{ deal }, isNew]) => {
      if (isNew) {
        this.inProgress$.next('save')
        this.goToDealId(deal.deal_id, false).finally(() => this.inProgress$.next(undefined))
      } else {
        this.dealForm.markAsPristine()
      }
    })

    this.actions$.pipe(
      ofType(sendConfirmationSuccess),
      withLatestFrom(this.dealId$),
      filter(([action, dealId]) => action.dealIds.includes(dealId)),
      switchMap(() => this.fetchDeal()),
      untilComponentDestroyed(this),
    ).subscribe(() => {
      this.dealForm.markAsPristine()
    })

    this.isNew$.pipe(switchMap(isNew => {
      if (isNew) return of()
      return merge(
        this.actions$.pipe(
          ofType(saveNoteSuccess),
          withLatestFrom(this.dealId$),
          filter(([action, dealId]) => notesTouchDeal(action, dealId)),
          switchMap(() => this.fetchDealNotes())),

        merge(
          this.actions$.pipe(
            ofType(
              actualizeCostToZeroSuccess,
              autosaveDealCostSuccess,
              autosaveDealSegmentSuccess,
              lockDealSuccess,
              saveDealFormSuccess,
              submitDealFormSuccess,
              unactualizeCostToZeroSuccess,
            )),
          this.actions$.pipe(
            ofType(sendConfirmationSuccess, escalateInvoicesSuccess),
            withLatestFrom(this.dealId$),
            filter(([action, dealId]) => action.dealIds.includes(dealId))),
          this.actions$.pipe(
            ofType(escalateInvoiceSuccess),
            withLatestFrom(this.dealId$),
            filter(([action, dealId]) => action.dealId === dealId)),
          this.actions$.pipe(
            ofType(setDealDateRangeSuccess),
            withLatestFrom(this.dealId$),
            filter(([action, dealId]) => action.deals[0].deal_id === dealId)),
          this.actions$.pipe(
            ofType(massDealFormSuccess),
            withLatestFrom(this.dealId$),
            filter(([action, dealId]) => !!action.deals.find(d => d.deal_id === dealId))),
        ).pipe(switchMap(() => this.fetchDeal())),
      )
    }), untilComponentDestroyed(this)).subscribe()

    this.actions$.pipe(
      ofType(copyCloneDealSuccess, cloneDealSuccess),
      map(a => a.deals),
      withLatestFrom(this.dealViewRaw$),
      untilComponentDestroyed(this),
    ).subscribe(([deals, dealViewRaw]) => {
      const deal = deals.find(d => d.deal_id === dealViewRaw.deal.deal_id)
      if (deal) this.dealViewRaw$.next({ ...dealViewRaw, deal })
    })

    this.dealViewRaw$.pipe(untilComponentDestroyed(this)).subscribe(dv =>
      this.DealForm.enableDisableDealForm(this.dealForm, dv))

    this.store.dispatch(loadAccounts({}))
    this.store.dispatch(loadProducts())
    this.store.dispatch(loadLocations({}))
    this.store.dispatch(loadCarriers({}))
    this.store.dispatch(loadCurrencies({}))
    this.store.dispatch(loadMeasures())

    const snapshotId$ = this.activeRoute.queryParams.pipe(
      map(q => q.snapshotId),
      distinctUntilChanged())

    this.dealId$.pipe(withLatestFrom(snapshotId$)).subscribe(([dealId, snapshotId]) => {
      if (dealId) {
        this.Offers.findSubOffer(dealId).then((supplierOffer) => this.supplierOffer = supplierOffer)
      } else if (!snapshotId) {
        this.newDeal()
      }
    })

    this.ready$.pipe(switchMap(() =>
      this.DealFormCalculator.keepTermDateInSync(this.dealViewRaw$, this.dealForm)),
      untilComponentDestroyed(this)).subscribe()

    this.activeRoute.queryParams.pipe(
      map(q => q.snapshotId),
      distinctUntilChanged(),
      filter(identity),
      switchMap(snapshotId => from(this.loadFromStorage(snapshotId))),
      untilComponentDestroyed(this)).subscribe()

    this.store.pipe(select(selectAllAccounts)).subscribe((accs) => {
      this.accounts = accs
    })
  }

  showAddSegment() {
    if (this.AuthApi.currentUser.role === 'trader') return
    combineLatest([
      this.dealViewRaw$.pipe(map(dto => last(dto.segments))),
      this.carriers$,
    ]).pipe(take(1)).subscribe(async ([lastSegment, carriers]) => {
      const { estimatedTotals: { weight } } = this.detailsForm.getRawValue()

      if (this.WA_13995_SHIPMENT_RATE_PICKER_UPGRADE) {
        const segment = await this.ShipmentRatePickerUpgraded.showAddSegment(lastSegment, carriers, weight)
        if (segment) this.createSegment(segment)
      } else {
        const segment = await this.ShipmentRatePicker.showAddSegment(lastSegment, carriers, weight)
        if (segment) this.createSegment(segment)
      }
    })
  }

  showDealDocuments() {
    this.dealId$.pipe(take(1), switchMap(dealId =>
      this.DocumentsOverlay.showDealDocuments(dealId, { canCreate: true }),
    )).subscribe()
  }

  setSpecialInstructions(party: DealPartyE.buyer | DealPartyE.supplier) {
    combineLatest([
      this.dealId$.pipe(take(1)),
      this.selectedDeals$,
    ])
      .subscribe(async ([dealId, deals]) => {
        const added = await this.NoteForm.showSetMultipleSpecialInstructions(deals, party)
        if (added && deals.some(deal => deal.deal.deal_id === dealId)) {
          this.fetchDealNotes().subscribe()
        }
      })
  }

  showChangeCloneDetails() {
    this.selectedDeals$.subscribe(deals => this.ChangeCloneForm.showChangeClone(deals))
  }

  changeCloneDeliveryLocation() {
    this.withCloneActionTargets(({ primary, deals }) => this.CloneActions.changeDeliveryLocation(primary, deals))
  }

  addCloneTertiaryCost() {
    this.withCloneActionTargets(({ primary, deals }) => this.CloneActions.addTertiaryCost(primary, deals))
  }

  addCloneFreightRates() {
    this.withCloneActionTargets(({ primary, deals }) => this.CloneActions.addFreightRates(primary, deals))
  }

  private withCloneActionTargets(action: (targets: { primary: DeepReadonly<Deal>, deals: DeepReadonly<Deal>[] }) => void) {
    combineLatest([this.dealRaw$, this.dealClones$.pipe(switchMap(c => c.selectedIds$))]).pipe(
      take(1),
      switchMap(([primary, selectedIds]) =>
        this.store.select(selectRawDealsByIds(selectedIds)).pipe(
          take(1),
          map(deals => ({ primary, deals: compact(deals) })),
        )),
    ).subscribe(async ({ primary, deals }) => {
      const label = (deal: DeepReadonly<Deal>) => deal.display_deal_id || deal.deal_id

      // Closed deals block too, but are bucketed separately so the toaster lists them under their own reason.
      const closed = deals.filter(deal => deal.status === DEAL_CLOSED)
      const disallowed = disallowedCloneActionDeals(deals.filter(deal => deal.status !== DEAL_CLOSED), this.AuthApi.currentUser)
      const tci = disallowed.filter(deal => deal.deal_id.endsWith('TCI')).map(label)
      const tc = disallowed.filter(deal => !deal.deal_id.endsWith('TCI')).map(label)

      if (closed.length || tci.length || tc.length) {
        const lines = ['This action cannot be completed. Remove the following deals and try again:', '']
        if (closed.length) lines.push(`Closed: ${closed.map(label).join(', ')}`)
        if (tci.length) lines.push(`Confirmed TCI: ${tci.join(', ')}`)
        if (tc.length) lines.push(`Confirmed non-inventory TC: ${tc.join(', ')}`)
        await this.ConfirmModal.show({
          title: 'Action cannot be completed',
          titleIcon: 'fas fa-exclamation-triangle',
          description: lines.join('\n'),
          confirmButtonText: 'Close',
          cancelButtonText: '',
        }).catch(() => undefined)
        return
      }

      if (!deals.length) return
      this.toaster.warning(`${deals.length} selected deal${deals.length > 1 ? 's' : ''} will be updated.`)
      action({ primary, deals })
    })
  }

  showReferenceNumbersModal() {
    combineLatest([
      this.dealClones$.pipe(
        switchMap(dealClones => dealClones.selectedIds$),
        take(1),
      ),
    ])
    .pipe(take(1))
    .subscribe(([dealIds]) => {
      this.DealReferenceNumbersService.showDealReferenceNumbers(dealIds)
    })
  }

  sendConfirmation(to: DealPartyE) {
    this.dealOrClones$.pipe(take(1)).subscribe(deals =>
      this.SendConfirmationForm.sendConfirmation(deals, to))
  }

  sendConfirmationLabel(to: DealPartyE) {
    return this.dealOrClones$.pipe(map(deals =>
      this.SendConfirmationForm.sendConfirmationLabel(deals, to)))
  }

  previewDocuments(to: DealPartyE) {
    this.fetchDealViewOrClones$.subscribe(deals =>
      this.DealDocuments.previewMergedDocuments(deals, to))
  }

  copyDeal() {
    this.dealViewRaw$
      .pipe(take(1))
      .subscribe(async (dv) => {
        let buyer = this.accounts.filter((acc) => acc.account.toString() === dv.deal.buyer_id)[0]
        let supplier = this.accounts.filter((acc) => acc.account.toString() === dv.deal.supplier_id)[0]
        let { notes } = dv
        if (buyer.status === -1 || supplier.status === -1) {
          this.toaster.error('Company in RESTRICTED status.')
          return null
        } else if (dv.deal.status === 'Draft' && ((dayjs.utc().unix() - dv.deal.created) > 3600 * 24 * 365)) {
          this.toaster.error("The deal you are trying to Copy has been in draft status for over a year. Please choose a more recent deal or create from scratch please.")
          return null
        }
        else {
          const [buyerPaymentTerms, supplierPaymentTerms] = await Promise.all([
            this.CreditPool.getPaymentTerms(dv.deal.buyer_id, DealPartyE.buyer),
            this.CreditPool.getPaymentTerms(dv.deal.supplier_id, DealPartyE.supplier)
          ])
          this.dealForm.controls.details.controls.buyerPaymentTerms.setValue(buyerPaymentTerms)
          this.dealForm.controls.details.controls.supplierPaymentTerms.setValue(supplierPaymentTerms)

          return this.DealFormSnapshots.copyDealForm(this.dealForm, { notes })
        }
      })
  }

  showCloneDeal() {
    this.dealViewRaw$.pipe(take(1)).subscribe(dv =>
      this.CloneForm.showCloneForm(dv))
  }

  // WA-22112: deal attachments tab
  private initAttachmentsTab() {
    this.isNew$.pipe(take(1), untilComponentDestroyed(this)).subscribe(isNew => {
      if (!isNew) {
        combineLatest([this.dealViewRaw$, this.ready$.pipe(filter(identity))])
          .pipe(take(1), untilComponentDestroyed(this))
          .subscribe(([dv]) => {
            this.attachmentsPicFileId = dv.deal?.product_pic_file_id
            this.attachmentsTechSpecFileId = dv.deal?.tech_spec_file_id
            this.attachmentFileIds$.next([...(dv.deal?.attachment_file_ids || [])])
            this.profilePrefillSub = this.initProfilePrefill(true)
          })
        return
      }

      // copy-deal navigates new → new?snapshotId=… reusing this component instance,
      // so every ready$ emission must be re-checked, not only the first
      this.ready$.pipe(filter(identity), untilComponentDestroyed(this)).subscribe(() => {
        const copiedIds = (this.detailsForm.controls.attachmentFileIds.value || []) as string[]
        this.profilePrefillSub?.unsubscribe()
        this.lastProfilePrefill = undefined
        if (copiedIds.length) {
          this.attachmentsPicFileId = this.detailsForm.controls.productPicFileId.value || undefined
          this.attachmentsTechSpecFileId = this.detailsForm.controls.techSpecFileId.value || undefined
          this.attachmentFileIds$.next([...copiedIds])
        }
        this.profilePrefillSub = this.initProfilePrefill(copiedIds.length > 0)
      })
    })
  }

  private profilePrefillSub: Subscription

  private initProfilePrefill(skipCurrent: boolean): Subscription {
    return combineLatest([
        replayForm(this.detailsForm.controls.supplierId),
        replayForm(this.productsForm),
      ]).pipe(
        map(([supplierId, products]) => ({
          supplierId,
          rows: (products || []).map(p => ({ productId: p.productId, itemTypeId: p.itemTypeId, suffix: p.suffix })),
        })),
        distinctUntilChanged(isEqual),
        skip(skipCurrent ? 1 : 0),
        switchMap(async source => this.currentAttachments?.some(f => !f.file_id) && !(await this.confirmReplaceAttachments()) ? null : source),
        filter(Boolean),
        // a save during the debounce window or a pending request must not reuse the previous selection's prefill
        tap(() => {
          this.lastProfilePrefill = undefined
          this.prefillAttachments$.next(null) // "checking" state until the lookup settles
        }),
        debounceTime(300),
        switchMap(({ supplierId, rows }) => this.ProductAttachments.buildProfilePrefill(supplierId, rows)),
        untilComponentDestroyed(this),
      ).subscribe(prefill => {
        this.lastProfilePrefill = prefill ?? undefined
        this.prefillAttachments$.next(prefill ?? { fileIds: [] })
      })
  }

  private confirmReplaceAttachments(): Promise<boolean> {
    return this.ConfirmModal.show({
      title: 'Replace attachments?',
      titleIcon: 'fas fa-exclamation-triangle',
      description: 'Changing the product replaces the attachments. The files you added will be lost.',
      confirmButtonText: 'Continue',
      confirmButtonClass: 'btn-primary',
      cancelButtonText: 'Stop',
      cancelButtonClass: 'btn-danger',
    }).then(() => true, () => false)
  }

  /**
   * WA-22112: uploads the new files of the attachments tab and writes the attachment
   * fields into the deal form, so serialize() carries them into the save payload.
   * Supplier profile files are replaced by copies the deal owns (WA-22667).
   */
  private async applyAttachmentsToForm(): Promise<boolean> {
    const canEdit = await this.canEditAttachments$.pipe(take(1)).toPromise()
    if (!canEdit) return true
    try {
      if (!this.currentAttachments) {
        // the lazy attachments tab was never opened — apply the profile prefill, or on a
        // new (copied) deal the attachments it was copied with
        const isNew = await this.isNew$.pipe(take(1)).toPromise()
        const { attachmentFileIds, productPicFileId, techSpecFileId } = this.detailsForm.getRawValue()
        const attachments = this.lastProfilePrefill
          ?? (isNew && attachmentFileIds?.length ? { fileIds: attachmentFileIds, productPicFileId, techSpecFileId } : undefined)
        if (attachments) {
          const own = await this.ProductAttachments.copyProfileFiles(attachments)
          // the copies are not profile files: a later save (or a retry) reuses them
          if (this.lastProfilePrefill) this.lastProfilePrefill = own
          this.detailsForm.patchValue({
            attachmentFileIds: own.fileIds,
            productPicFileId: own.productPicFileId || '',
            techSpecFileId: own.techSpecFileId || '',
          })
        }
        return true
      }
      const attachmentFileIds = await this.ProductAttachments.persistFileInfos(this.currentAttachments, {
        uploadPayload: fileInfo => ({ visibility: 1, description: fileInfo.description || undefined }),
        copyProfileFiles: true,
      })
      this.detailsForm.patchValue({
        attachmentFileIds,
        productPicFileId: this.ProductAttachments.getProductPicFileId(this.currentAttachments) || '',
        techSpecFileId: this.ProductAttachments.getTechSpecFileId(this.currentAttachments) || '',
      })
      return true
    } catch (err) {
      console.error('Unable to upload deal attachments', err)
      this.toaster.error('Unable to upload deal attachments', err)
      return false
    }
  }

  // inProgress$ engages only after the form action is dispatched, but attachments are
  // uploaded before that — this latch closes the double-click window in between
  private persistingAttachments = false

  override async saveDeal() {
    if (this.inProgress$.value || this.persistingAttachments) return
    if (this.alertIfDealFormInvalid()) return
    this.persistingAttachments = true
    try {
      if (!(await this.applyAttachmentsToForm())) return
      return await super.saveDeal()
    } finally {
      this.persistingAttachments = false
    }
  }

  submitDeal() {
    if (this.inProgress$.value || this.persistingAttachments) return
    if (this.alertIfDealFormInvalid()) return

    combineLatest([this.dealId$, this.isClone$, this.isClonesTabSelected$, this.dealViewRaw$]).pipe(take(1))
      .subscribe(async ([dealId, isClone, isClonesTabSelected, dv]) => {
        this.persistingAttachments = true
        try {
          if (isClone && isClonesTabSelected) {
            await this.ConfirmModal.show({
              title: 'Deal Confirmation',
              titleIcon: 'fas fa-exclamation-triangle',
              description: `
              Please confirm that you only want to send out confirmations for the
              deal <strong>${dealId}</strong> (the one from which you are accessing the Clones tab).<br><br>
              If you wanted to send confirmataions for the deals you selected,
              please use links in the <strong>ACTION</strong> button.`,
              confirmButtonText: 'Send Confirmations',
              confirmButtonClass: 'btn-primary',
              cancelButtonText: 'Cancel',
              cancelButtonClass: 'btn-danger',
            })
          }
          if (!(await this.applyAttachmentsToForm())) return
          this.store.dispatch(submitDealForm({
            dv,
            dealForm: this.dealForm.serialize(),
            skipConfirmationDialog: true,
          }))
        } finally {
          this.persistingAttachments = false
        }
      })
  }

  async goToShippingView() {
    if (this.dealForm.dirty && this.dealForm.touched) await this.ConfirmModal.showUnsavedDataWarning()
    this.dealId$.pipe(take(1)).subscribe(dealId =>
      this.router.navigateByUrl(`/logistics/shipping-log/${dealId}?back=${this.back}`))
  }

  setActiveTab(index: number) {
    const tabName = TABS[index]
    this.activeTab$.next(tabName)
    if (!this.modalDisplay) {
      location.hash = tabName === 'details' ? '' : tabName
    }
  }

  protected isViewingProductSpecsTab(): boolean {
    return this.activeTab$.value === 'product-specs'
  }

  showChangeParty(party: DealPartyE.buyer | DealPartyE.supplier) {
    this.selectedDeals$.subscribe(deals =>
      this.ChangePartyForm.showChangeParty(deals, party))
  }

  showChangeBrokerageParty() {
    this.selectedDeals$.subscribe(deals =>
      this.ChangeBrokeragePartyForm.showChangeBrokerageParty(deals))
  }

  loadingMenu = false;

  async onActionClick(event: MouseEvent, trigger: MatMenuTrigger) {
    event.preventDefault();
    event.stopPropagation();

    if (this.loadingMenu) return;
    this.loadingMenu = true;

    this.dealRaw$
    .pipe(
      take(1),
      switchMap(deal => this.Offers.findSubOffer(deal.deal_id)),
      take(1)
    )
    .subscribe({
      next: item => {
        this.supplierOffer = item;
        trigger.openMenu();
        this.loadingMenu = false;
      },
      error: () => {
        trigger.openMenu();
        this.loadingMenu = false;
      }
    });
  }

  showCreateOffer() {
    this.dealViewRaw$.pipe(take(1)).subscribe(dv => {
      this.CreateOfferForm.showCreateOffer(dv).then(so => {
        if (so) this.supplierOffer = so
      });
    });
  }

  async showSupplierOffer() {
    this.SupplierOfferOverlayService.editOffer(this.supplierOffer).subscribe()
  }

  showMatchedOffers() {
    this.router.navigateByUrl(`/trading/matched-offers?supplierOffersIds=${this.supplierOffer.offer_id}`)
  }

  showExportGrid() {
    const dataSource = this.dealClones.dataSource
    const selectedIds = this.dealClones.selectedIds$.value
    let selected: DealRow[]
    const cloneIds = this.dealClones.orderedIds
    this.store.select(selectDealsByIds(selectedIds)).pipe(take(1)).subscribe(deals =>
      selected = compact(deals) as DealRow[])
    if (!cloneIds.length) return

    const currentSort = dataSource.sorttt;
    const sortActive = currentSort?.active;
    const sortDirection = currentSort?.direction;
    const rank = new Map(cloneIds.map((dealId, index) => [dealId, index]))

    const backendSorted = sortActive && !FRONTEND_SORT_COLUMNS.includes(sortActive)

    const applySorting = (data: DealRow[]) => {
      if (!backendSorted) {
        return sortBy(data, row => rank.has(row.deal_id) ? rank.get(row.deal_id) : cloneIds.length);
      } else if (sortActive && sortDirection && dataSource.sortingDataAccessor) {
        return orderBy(
          data,
          [(item) => dataSource.sortingDataAccessor(item, sortActive)],
          [sortDirection as 'asc' | 'desc']
        );
      } else {
        return sortBy(data, 'seq');
      }
    };

    return this.CsvExporter.fromDataTable({
      dataSource,
      fileName: `Deal-Clones-${new Date()}`,
      selected,
      visible: this.dealClones.exportColumns,
      available: this.dealClones.exportColumns,
      columnDefs: columnDefsById(TableKey.DealClonesTab),
      loadAll: async (columns) => {
        const deals = await this.elastic.fetchAllDeals({
          deal_ids: cloneIds,
          columns: uniq([...columns, 'bids', 'offers', 'segments']), // NOTE: need extra data for `closingDate` column
        }).toPromise();

        return applySorting(deals);
      },
      loadByIds: async (rows, columns) => {
        const deals = await this.elastic.fetchAllDeals({
          deal_ids: rows.map(r => r.deal_id),
          columns: uniq([...columns, 'bids', 'offers', 'segments']), // NOTE: need extra data for `closingDate` column
        }).toPromise();

        return applySorting(deals);
      }
    })
  }

  private async loadFromStorage(snapshotId: string) {
    const { dealViewRaw, snapshot } = await this.DealFormSnapshots.restoreSnapshot(snapshotId)
    snapshot.details.date = dayjs().unix()
    this.DealForm.setValue(this.dealForm, snapshot)
    this.DealFormCalculator.syncTermDate(dealViewRaw, this.dealForm).subscribe(() => {
      this.ready$.next(true)
      this.dealViewRaw$.next(dealViewRaw)
    })
  }

  private async newDeal() {
    const { dealViewRaw, dealProducts } = await this.DealFormSnapshots.newDeal()
    this.DealForm.patchForm(this.dealForm, dealViewRaw, dealProducts)
    this.ready$.next(true)
    this.dealViewRaw$.next(dealViewRaw)
  }

  private fetchDealViews(dealIds: string[], parts = [ 'deal', 'offers', 'bids', 'costs', 'credit_notes', 'vendor_credits', 'invoices', 'segments', 'notes', 'files' ] as const) {
    return this.Deals.getDealViews(dealIds, parts).toPromise()
  }
}

// compact() - the notes API answers 200 with data:null on failure, and one null reaching the
// caller's .some() would throw and tear down its subscription for the rest of the page's life
export function notesTouchDeal(
  { note, notes }: { note?: DeepReadonly<Note>, notes?: DeepReadonly<Note[]> },
  dealId: string,
) {
  return compact(notes || [note]).some(n => n.deal_id === dealId)
}
